import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Menu,
  X,
  BarChart2,
  User,
  Users,
  Calendar,
  MessageCircle,
  Sparkles,
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import ChatNotificationIcon from "@/components/chat/ChatNotificationIcon";
import HelpGuideWidget from "@/components/guide/HelpGuideWidget";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import ProfileSettingsWidget from "@/components/settings/ProfileSettingsWidget";
import VoiceCheckInWidget from "@/components/voice/VoiceCheckInWidget";
import Footer from "@/components/Footer";

type NavItemProps = {
  to: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
};

const NavItem = ({ to, icon, label, active }: NavItemProps) => (
  <Link to={to}>
    <Button
      variant="ghost"
      className={`w-full justify-start gap-3 mb-1 h-11 text-base ${
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      <span>{label}</span>
    </Button>
  </Link>
);

const Layout = ({ children }: { children: React.ReactNode }) => {
  const [navOpen, setNavOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const { user } = useUser();

  const toggleNav = () => setNavOpen(!navOpen);

  const navItems = [
    { to: "/", icon: <BarChart2 size={18} />, label: "Dashboard" },
    { to: "/check-in", icon: <Calendar size={18} />, label: "My Check-ins" },
    { to: "/patterns", icon: <Sparkles size={18} />, label: "AI Insights" },
    { to: "/profile", icon: <User size={18} />, label: "My Growth" },
    { to: "/team", icon: <Users size={18} />, label: "Team" },
    { to: "/chat", icon: <MessageCircle size={18} />, label: "Chat" },
  ];

  // Fetch unread message count
  useEffect(() => {
    /*
    const fetchUnreadCount = async () => {
      if (!user?.id) return;

      try {
        // In a real app, this would query the database for unread messages
        // For this demo we'll use the chat_messages table and check for unread messages
        const { data, error } = await supabase
          .from("chat_messages")
          .select("count", { count: "exact" })
          .eq("recipient_id", user.id)
          .eq("read", false)
          .limit(1);

        if (!error) {
          // Parse the count or get a random number for demonstration
          const count =
            data?.length > 0
              ? parseInt(String(data[0]?.count || "0"))
              : Math.floor(Math.random() * 5);
          setUnreadCount(count);
        }

        // Set up a subscription to update the unread count in real-time
        const subscription = supabase
          .channel("unread_messages")
          .on(
            "postgres_changes",
            {
              event: "INSERT",
              schema: "public",
              table: "chat_messages",
              filter: `recipient_id=eq.${user.id}`,
            },
            () => {
              // Increment the unread count when a new message arrives
              setUnreadCount((prev) => prev + 1);
            }
          )
          .subscribe();

        // Cleanup the subscription when the component unmounts
        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error fetching unread count:", error);
        // Set a random count for demonstration
        setUnreadCount(Math.floor(Math.random() * 5));
      }
    };

    fetchUnreadCount();
    */
  }, [user?.id]);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Nav Toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 md:hidden h-11 w-11 bg-background/80 backdrop-blur-sm border shadow-sm"
        onClick={toggleNav}
      >
        {navOpen ? <X size={20} /> : <Menu size={20} />}
      </Button>

      {/* Mobile overlay */}
      {navOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden" 
          onClick={() => setNavOpen(false)}
        />
      )}

      {/* Sidebar/Navigation */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 transform bg-card shadow-lg transition-transform duration-200 md:sticky md:top-0 md:h-screen md:translate-x-0 ${
          navOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 items-center justify-between border-b px-6">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary"></div>
            <h1 className="text-xl font-bold">Step</h1>
          </div>
          <ThemeToggle />
        </div>

        <div className="space-y-4 p-4">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                active={location.pathname === item.to}
              />
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-x-hidden relative flex flex-col">
        <main className="flex-1 p-4 md:p-8">{children}</main>
        
        {/* Footer */}
        <Footer />
        
        {/* Fixed widgets in bottom corners */}
        <div className="fixed bottom-6 left-6 z-50 flex gap-3">
          <NotificationBell />
          <ProfileSettingsWidget />
          <VoiceCheckInWidget />
        </div>

        {/* Fixed Help Guide Widget in bottom right */}
        <div className="fixed bottom-6 right-6 z-50">
          <HelpGuideWidget />
        </div>
      </div>
    </div>
  );
};

export default Layout;
